import { FC, memo, useEffect } from 'react'

import GridCard from '../../common/grid-card'
import { ComponentSeparator } from '../styles'
import { Title, TitleContainer, Section } from './styles'
import { useTranslation } from '../../../hooks/locale'
import { useRequestUserConfigContext } from '../../../hooks/request'
import { listingClient } from '../../../clients'
import { SliderContainer } from '../../common/listing-card-section/styles'
import PropertyGridSkeleton from '../../home/continue-search-widget/skeleton'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { useResponsiveValue } from '../../../hooks/responsive'
import { useAsyncCallback } from '../../../hooks/async'
import { cardEventCategories } from '../../../services/analytics-service/categories'
import AnalyticsService from '../../../services/analytics-service'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import { EmblaCarousel } from '../../common/embla-carousel'

interface SimilarPropertyProps {
    listingId: number
    regionId: number
    filter: string
    arrival?: string
    departure?: string
    persons?: number
    url?: string
    breakpoints?: { query: string; value: number }[]
    pageType: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const DEFAULT_SIMILAR_PROPERTY_BREAKPOINTS = [
    { query: '(min-width: 768px)', value: 2 },
    { query: '(max-width: 767px)', value: 1.5 },
]

const SimilarProperty: FC<SimilarPropertyProps> = ({
    listingId,
    regionId,
    filter,
    arrival,
    departure,
    persons,
    url,
    breakpoints = DEFAULT_SIMILAR_PROPERTY_BREAKPOINTS,
    pageType,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}) => {
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()

    const visibleSlides = useResponsiveValue(breakpoints, 1.5)

    useEffect(() => {
        AnalyticsService.trackViewEvent(pageType, cardEventCategories.sections.SIMILAR_PROPERTIES)
    }, [pageType])

    const loadSimilar = useAsyncCallback(async () => {
        const params = {
            filter,
            listingId,
            regionId,
            limit: 12,
            ...(arrival && { arrival }),
            ...(departure && { departure }),
            ...(typeof persons === 'number' && persons > 0 && { persons }),
        }

        const res = await listingClient.getPdpRecommendedObjects({
            user,
            params,
        })

        return res?.data?.objects ?? []
    }, [])

    useEffect(() => {
        if (listingId && regionId) {
            loadSimilar.callback()
        }
    }, [listingId, regionId, filter, arrival, departure, persons])

    const isLoading = loadSimilar.isLoading
    const property = loadSimilar.data as ListingSectionObject[] | null

    if (!isLoading && (!property || property.length === 0)) {
        return null
    }

    return (
        <Section id="similar-properties">
            <TitleContainer>
                <Title>{t('accomodations.similarPropertySectionTitle')}</Title>
            </TitleContainer>

            {isLoading && (
                <>
                    <PropertyGridSkeleton count={2} />
                    <ComponentSeparator />
                </>
            )}

            {!isLoading && property && property.length > 0 && (
                <>
                    <SliderContainer>
                        <EmblaCarousel
                            slides={property.map((item, index) => (
                                <GridCard
                                    key={item.id}
                                    index={index}
                                    listingObject={item}
                                    requestUrl={url}
                                    pageName={pageType}
                                    sectionName={cardEventCategories.sections.SIMILAR_PROPERTIES}
                                    breadcrumbModel={breadcrumbModel}
                                    offerModel={offerModel}
                                    suitablesModel={suitablesModel}
                                />
                            ))}
                            visibleSlides={visibleSlides}
                            slidesToScroll={Math.floor(visibleSlides)}
                            showNav
                            navVariant="top-right"
                            navPosition={{
                                desktopTop: '-3.5rem',
                                desktopRight: '0',
                                wideTop: '-3.5rem',
                                wideRight: '0',
                            }}
                        />
                    </SliderContainer>

                    <ComponentSeparator />
                </>
            )}
        </Section>
    )
}

export default memo(SimilarProperty)
